import { FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "@/lib/supabase";
import { adminAddNewSecondaryButtonClass } from "@/lib/adminSectionChrome";
import {
  PORTAL_CAPABILITIES,
  type PortalCapability,
  type PortalGrantRow,
} from "@/types/portalUser";

type BranchOpt = { id: string; name: string };
type BatchOpt = { id: string; label: string };
type AssocOpt = { id: string; name: string };

/** Form: who is this person — drives which fields are shown. */
type AccessKind = "" | "portal_manager" | "site_content_admin" | "tasks_access" | "scoped";

const CAP_LABELS: Record<PortalCapability, string> = {
  manage_users: "Portal manager",
  site_admin: "Site-wide content admin",
  tasks: "Tasks access",
  branch_profile: "Branch: edit page",
  branch_news: "Branch: news",
  branch_projects: "Branch: projects",
  batch_profile: "Batch: edit page",
  batch_news: "Batch: news",
  batch_projects: "Batch: projects",
  association_profile: "Association: edit page",
  association_news: "Association: news",
  association_projects: "Association: projects",
};

/** Scoped editors get full page + news + projects for each entity they are assigned to (no per-permission toggles). */
const ALL_BRANCH_CAPS: PortalCapability[] = ["branch_profile", "branch_news", "branch_projects"];
const ALL_BATCH_CAPS: PortalCapability[] = ["batch_profile", "batch_news", "batch_projects"];
const ALL_ASSOC_CAPS: PortalCapability[] = ["association_profile", "association_news", "association_projects"];

function grantsToPayload(
  accessKind: AccessKind,
  branchIds: string[],
  batchIds: string[],
  assocIds: string[],
): { capability: PortalCapability; branch_id?: string; batch_id?: string; association_id?: string }[] {
  if (accessKind === "portal_manager") return [{ capability: "manage_users" }];
  if (accessKind === "site_content_admin") return [{ capability: "site_admin" }];
  if (accessKind === "tasks_access") return [{ capability: "tasks" }];
  if (accessKind !== "scoped") return [];

  const out: { capability: PortalCapability; branch_id?: string; batch_id?: string; association_id?: string }[] = [];

  const branchId = branchIds[0];
  if (branchId) {
    for (const c of ALL_BRANCH_CAPS) out.push({ capability: c, branch_id: branchId });
  }

  const batchId = batchIds[0];
  if (batchId) {
    for (const c of ALL_BATCH_CAPS) out.push({ capability: c, batch_id: batchId });
  }

  for (const aid of assocIds) {
    for (const c of ALL_ASSOC_CAPS) out.push({ capability: c, association_id: aid });
  }
  return out;
}

function payloadFromRows(rows: PortalGrantRow[]): Map<string, PortalGrantRow[]> {
  const m = new Map<string, PortalGrantRow[]>();
  for (const r of rows) {
    const list = m.get(r.email) ?? [];
    list.push(r);
    m.set(r.email, list);
  }
  return m;
}

/** One-line explanation for the access cards (not the raw grant list). */
function summarizePersonAccess(
  list: PortalGrantRow[],
  branches: BranchOpt[],
  batches: BatchOpt[],
  assocOpts: AssocOpt[],
): string {
  if (list.some((g) => g.capability === "manage_users")) {
    return "Portal manager — full org access and can add or remove admin users.";
  }
  if (list.some((g) => g.capability === "site_admin")) {
    return "Site-wide content — can edit everything except who has admin access.";
  }
  if (list.some((g) => g.capability === "tasks")) {
    return "Tasks access — can view and update the shared tasks list.";
  }
  const parts: string[] = [];
  const br = list.find((g) => g.branch_id && g.capability.startsWith("branch_"));
  if (br?.branch_id) {
    const name = branches.find((b) => b.id === br.branch_id)?.name ?? "a branch";
    parts.push(`branch: ${name}`);
  }
  const ba = list.find((g) => g.batch_id && g.capability.startsWith("batch_"));
  if (ba?.batch_id) {
    const name = batches.find((b) => b.id === ba.batch_id)?.label ?? "a batch";
    parts.push(`batch: ${name}`);
  }
  const assocIds = new Set(
    list.map((g) => g.association_id).filter((id): id is string => id != null && id !== ""),
  );
  if (assocIds.size > 0) {
    const names = [...assocIds]
      .map((id) => assocOpts.find((a) => a.id === id)?.name)
      .filter(Boolean) as string[];
    if (assocIds.size === 1 && names[0]) parts.push(`association: ${names[0]}`);
    else
      parts.push(
        `${assocIds.size} associations${names.length ? ` (${names.slice(0, 3).join(", ")}${names.length > 3 ? "…" : ""})` : ""}`,
      );
  }
  if (parts.length === 0) return "See permission details below.";
  return `Scoped editor — ${parts.join(" · ")}.`;
}

export function UsersTab() {
  const [rows, setRows] = useState<PortalGrantRow[]>([]);
  const [branches, setBranches] = useState<BranchOpt[]>([]);
  const [batches, setBatches] = useState<BatchOpt[]>([]);
  const [associations, setAssociations] = useState<AssocOpt[]>([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState("");

  const [editingEmail, setEditingEmail] = useState<string | null>(null);
  const [emailInput, setEmailInput] = useState("");
  const [accessKind, setAccessKind] = useState<AccessKind>("");
  const [branchIds, setBranchIds] = useState<string[]>([]);
  const [batchIds, setBatchIds] = useState<string[]>([]);
  const [assocIds, setAssocIds] = useState<string[]>([]);

  const [search, setSearch] = useState("");
  const [branchScopeFilter, setBranchScopeFilter] = useState("");
  const [batchScopeFilter, setBatchScopeFilter] = useState("");
  const [assocScopeFilter, setAssocScopeFilter] = useState("");
  const [capFilter, setCapFilter] = useState<PortalCapability | "all">("all");

  const loadRefs = useCallback(async () => {
    const [br, ba, as] = await Promise.all([
      supabase.from("branches").select("id,name").order("name"),
      supabase.from("batches").select("id,slug,official_title,custom_title,al_year").order("al_year", { ascending: false }),
      supabase.from("associations").select("id,name").order("name"),
    ]);
    setBranches((br.data as BranchOpt[]) ?? []);
    setBatches(
      ((ba.data ?? []) as { id: string; slug: string; official_title: string | null; custom_title: string | null; al_year: number | null }[]).map(
        (b) => ({
          id: b.id,
          label: b.custom_title || b.official_title || b.slug || String(b.al_year ?? ""),
        })
      )
    );
    setAssociations((as.data as AssocOpt[]) ?? []);
  }, []);

  const loadGrants = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("portal_access_grants")
      .select("id,email,capability,branch_id,batch_id,association_id,created_at")
      .order("email")
      .order("created_at");
    if (error) {
      console.error(error);
      setRows([]);
    } else {
      setRows((data as PortalGrantRow[]) ?? []);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    void loadRefs();
    void loadGrants();
  }, [loadRefs, loadGrants]);

  const byEmail = useMemo(() => payloadFromRows(rows), [rows]);

  const sortedPeople = useMemo(
    () => Array.from(byEmail.entries()).sort(([a], [b]) => a.localeCompare(b)),
    [byEmail],
  );

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (capFilter !== "all" && r.capability !== capFilter) return false;
      if (branchScopeFilter && (r.branch_id !== branchScopeFilter || !r.capability.startsWith("branch_"))) {
        return false;
      }
      if (batchScopeFilter && (r.batch_id !== batchScopeFilter || !r.capability.startsWith("batch_"))) {
        return false;
      }
      if (
        assocScopeFilter &&
        (r.association_id !== assocScopeFilter || !r.capability.startsWith("association_"))
      ) {
        return false;
      }
      if (!q) return true;
      if (r.email.includes(q)) return true;
      if (CAP_LABELS[r.capability].toLowerCase().includes(q)) return true;
      const bn = branches.find((b) => b.id === r.branch_id)?.name ?? "";
      const bat = batches.find((b) => b.id === r.batch_id)?.label ?? "";
      const an = associations.find((a) => a.id === r.association_id)?.name ?? "";
      if (bn.toLowerCase().includes(q) || bat.toLowerCase().includes(q) || an.toLowerCase().includes(q)) return true;
      return false;
    });
  }, [rows, search, capFilter, branchScopeFilter, batchScopeFilter, assocScopeFilter, branches, batches, associations]);

  /** People cards follow the same rules as the raw table so one filter set drives both. */
  const filteredPeople = useMemo(() => {
    const emails = new Set(filteredRows.map((r) => r.email));
    return sortedPeople.filter(([email]) => emails.has(email));
  }, [sortedPeople, filteredRows]);

  const hasActiveFilters =
    search.trim() !== "" ||
    capFilter !== "all" ||
    !!branchScopeFilter ||
    !!batchScopeFilter ||
    !!assocScopeFilter;

  function startNew() {
    setEditingEmail(null);
    setEmailInput("");
    setAccessKind("");
    setBranchIds([]);
    setBatchIds([]);
    setAssocIds([]);
    setMsg("");
  }

  function startEdit(email: string) {
    const list = byEmail.get(email) ?? [];
    setEditingEmail(email);
    setEmailInput(email);

    const hasManage = list.some((g) => g.capability === "manage_users");
    const hasSite = list.some((g) => g.capability === "site_admin");
    const hasBranch = list.some((g) => g.capability.startsWith("branch_"));
    const hasBatch = list.some((g) => g.capability.startsWith("batch_"));
    const hasAssoc = list.some((g) => g.capability.startsWith("association_"));

    const hasScoped = hasBranch || hasBatch || hasAssoc;

    if (hasManage) {
      setAccessKind("portal_manager");
      setBranchIds([]);
      setBatchIds([]);
      setAssocIds([]);
      setMsg(
        hasScoped
          ? "This account also had scoped grants; those are ignored while type is Portal manager. Save to align."
          : "",
      );
      return;
    }

    if (hasSite) {
      setAccessKind("site_content_admin");
      setBranchIds([]);
      setBatchIds([]);
      setAssocIds([]);
      setMsg(
        hasScoped
          ? "This account also had scoped grants; those are ignored while type is Site-wide content admin. Save to align."
          : "",
      );
      return;
    }

    const bIds = new Set<string>();
    const baIds = new Set<string>();
    const aIds = new Set<string>();
    for (const g of list) {
      if (g.branch_id) bIds.add(g.branch_id);
      if (g.batch_id) baIds.add(g.batch_id);
      if (g.association_id) aIds.add(g.association_id);
    }

    setAccessKind("scoped");
    setMsg("");
    setBranchIds(bIds.size ? [...bIds].slice(0, 1) : []);
    setBatchIds(baIds.size ? [...baIds].slice(0, 1) : []);
    setAssocIds([...aIds]);
  }

  function chooseAccessKind(next: AccessKind) {
    setAccessKind(next);
    setMsg("");
    if (next === "portal_manager") {
      setBranchIds([]);
      setBatchIds([]);
      setAssocIds([]);
    } else if (next === "site_content_admin") {
      setBranchIds([]);
      setBatchIds([]);
      setAssocIds([]);
    } else if (next === "scoped") {
      setBranchIds([]);
      setBatchIds([]);
      setAssocIds([]);
    } else {
      setBranchIds([]);
      setBatchIds([]);
      setAssocIds([]);
    }
  }

  function validate(): string | null {
    const e = emailInput.trim().toLowerCase();
    if (!e) return "Email is required.";
    if (!accessKind) return "Choose an access type.";

    if (accessKind === "portal_manager" || accessKind === "site_content_admin" || accessKind === "tasks_access") {
      return null;
    }

    const hasBranch = branchIds.length === 1;
    const hasBatch = batchIds.length === 1;
    const hasAssoc = assocIds.length >= 1;
    if (!hasBranch && !hasBatch && !hasAssoc) {
      return "Select at least one branch, one batch, or one or more associations. Full editing is granted automatically for each place you choose.";
    }

    return null;
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setMsg("");
    const err = validate();
    if (err) {
      setMsg(err);
      return;
    }
    const email = emailInput.trim().toLowerCase();
    const payload = grantsToPayload(accessKind, branchIds, batchIds, assocIds);
    const { error } = await supabase.rpc("replace_portal_access_grants", {
      p_email: email,
      p_grants: payload,
    });
    if (error) {
      setMsg(error.message);
      return;
    }
    setMsg("Saved.");
    await loadGrants();
    startEdit(email);
  }

  async function onDeleteUser(email: string) {
    if (!confirm(`Remove all portal access for ${email}?`)) return;
    const { error } = await supabase.rpc("replace_portal_access_grants", {
      p_email: email,
      p_grants: [],
    });
    if (error) {
      setMsg(error.message);
      return;
    }
    await loadGrants();
    if (editingEmail === email) startNew();
  }

  function scopeLine(r: PortalGrantRow): string {
    if (r.branch_id) {
      const n = branches.find((b) => b.id === r.branch_id)?.name ?? r.branch_id.slice(0, 8);
      return `Branch: ${n}`;
    }
    if (r.batch_id) {
      const n = batches.find((b) => b.id === r.batch_id)?.label ?? r.batch_id.slice(0, 8);
      return `Batch: ${n}`;
    }
    if (r.association_id) {
      const n = associations.find((a) => a.id === r.association_id)?.name ?? r.association_id.slice(0, 8);
      return `Association: ${n}`;
    }
    return "—";
  }

  const showScoped = accessKind === "scoped";

  return (
    <div className="w-full max-w-7xl text-brand-blue">
      <h2 className="font-display text-xl font-bold">Portal access</h2>
      <p className="mt-2 max-w-3xl text-sm text-brand-blue/80">
        Pick how this person uses the admin portal first. Portal manager and Site-wide content admin can work across the whole
        site. A scoped editor is tied to specific places: one branch, one batch, and/or one or more associations (any mix). Each
        place gets full editing for page, news, and projects—no separate permission ticks. Saving replaces all grants for that email.
      </p>

      {loading ? <p className="mt-4 text-sm">Loading…</p> : null}

      <div className="mt-6 grid gap-6 lg:grid-cols-2 lg:items-start lg:gap-8">
        <div className="min-w-0 space-y-6">
        <section
          aria-label="Filter people with access"
          className="rounded-xl border border-brand-gray bg-white p-4 shadow-sm"
        >
        <h3 className="font-display text-base font-bold text-brand-blue">Search and filter</h3>
        <p className="mt-1 text-xs text-brand-blue/70">
          Same rules for the people list below and the optional raw table. The add / edit form is in the column to the right on wide
          screens.
        </p>
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          <label className="block text-xs font-semibold">
            Capability
            <select
              className="mt-1 block w-full rounded border border-brand-gray px-2 py-1.5 text-sm"
              value={capFilter}
              onChange={(e) => setCapFilter(e.target.value as PortalCapability | "all")}
            >
              <option value="all">All</option>
              {PORTAL_CAPABILITIES.map((c) => (
                <option key={c} value={c}>
                  {CAP_LABELS[c]}
                </option>
              ))}
            </select>
          </label>
          <label className="block text-xs font-semibold">
            Branch scope
            <select
              className="mt-1 block w-full rounded border border-brand-gray px-2 py-1.5 text-sm"
              value={branchScopeFilter}
              onChange={(e) => setBranchScopeFilter(e.target.value)}
            >
              <option value="">Any</option>
              {branches.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
          </label>
          <label className="block text-xs font-semibold">
            Batch scope
            <select
              className="mt-1 block w-full rounded border border-brand-gray px-2 py-1.5 text-sm"
              value={batchScopeFilter}
              onChange={(e) => setBatchScopeFilter(e.target.value)}
            >
              <option value="">Any</option>
              {batches.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.label}
                </option>
              ))}
            </select>
          </label>
          <label className="block text-xs font-semibold sm:col-span-2">
            Association scope
            <select
              className="mt-1 block w-full rounded border border-brand-gray px-2 py-1.5 text-sm"
              value={assocScopeFilter}
              onChange={(e) => setAssocScopeFilter(e.target.value)}
            >
              <option value="">Any</option>
              {associations.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </select>
          </label>
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search email, capability, or entity…"
            className="min-w-[12rem] flex-1 rounded border border-brand-gray px-3 py-2 text-sm"
          />
          {hasActiveFilters ? (
            <button
              type="button"
              className="text-xs font-semibold text-brand-maroon underline"
              onClick={() => {
                setSearch("");
                setCapFilter("all");
                setBranchScopeFilter("");
                setBatchScopeFilter("");
                setAssocScopeFilter("");
              }}
            >
              Clear filters
            </button>
          ) : null}
        </div>
        <p className="mt-2 text-xs text-brand-blue/60">
          {filteredRows.length} permission row{filteredRows.length === 1 ? "" : "s"} shown
          {rows.length !== filteredRows.length ? ` (of ${rows.length} total)` : ""}
          {hasActiveFilters ? " — filters active" : ""}
        </p>
        </section>

        <section className="rounded-xl border border-brand-gray bg-gradient-to-b from-white to-brand-gray/5 p-5 shadow-sm">
        <h3 className="font-display text-lg font-bold text-brand-blue">People with admin access</h3>
        <p className="mt-1 text-sm text-brand-blue/75">
          One card per person with a plain-language summary. Open <span className="font-semibold text-brand-blue">Raw permission rows</span>{" "}
          only if you need the exact database lines (e.g. auditing). Use <span className="font-semibold text-brand-blue">Edit in form</span>{" "}
          to change someone, or remove their access.
        </p>
        {sortedPeople.length === 0 ? (
          <p className="mt-6 rounded-lg border border-dashed border-brand-gray bg-white/80 px-4 py-8 text-center text-sm text-brand-blue/70">
            No one has portal access yet. Add an email and save using the access form.
          </p>
        ) : filteredPeople.length === 0 ? (
          <p className="mt-6 rounded-lg border border-dashed border-brand-gray bg-white/80 px-4 py-8 text-center text-sm text-brand-blue/70">
            No one matches the current filters. Try clearing filters or changing the search.
          </p>
        ) : (
          <ul className="mt-4 grid max-h-[min(28rem,70vh)] gap-3 overflow-y-auto pr-1 sm:grid-cols-1">
            {filteredPeople.map(([email, list]) => (
              <li
                key={email}
                className={`flex flex-col gap-3 rounded-lg border border-brand-gray/90 bg-white p-4 shadow-sm sm:flex-row sm:items-center sm:justify-between ${
                  editingEmail === email ? "ring-2 ring-brand-maroon/40" : ""
                }`}
              >
                <div className="min-w-0 flex-1">
                  <p className="truncate font-display text-base font-bold text-brand-blue">{email}</p>
                  <p className="mt-1 text-sm leading-snug text-brand-blue/80">
                    {summarizePersonAccess(list, branches, batches, associations)}
                  </p>
                  <p className="mt-2 text-xs text-brand-blue/55">
                    {list.length} stored permission{list.length === 1 ? "" : "s"}
                  </p>
                </div>
                <div className="flex shrink-0 flex-wrap gap-2 sm:flex-col sm:items-stretch sm:justify-center">
                  <button
                    type="button"
                    className="rounded-full border border-brand-maroon bg-brand-maroon px-4 py-2 text-center text-sm font-semibold text-white transition hover:bg-brand-maroon/90"
                    onClick={() => startEdit(email)}
                  >
                    Edit in form
                  </button>
                  <button
                    type="button"
                    className="rounded-full border border-red-300 bg-white px-4 py-2 text-center text-sm font-semibold text-red-800 transition hover:bg-red-50"
                    onClick={() => void onDeleteUser(email)}
                  >
                    Remove all access
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}

        {rows.length > 0 ? (
          <details className="mt-6 rounded-lg border border-brand-gray/80 bg-white/90 px-3 py-2 shadow-sm open:pb-3">
            <summary className="cursor-pointer list-none py-2 font-display text-sm font-bold text-brand-blue marker:content-none [&::-webkit-details-marker]:hidden">
              Raw permission rows (optional)
            </summary>
            <p className="mb-3 text-xs text-brand-blue/70">
              Same filters as above apply here. Each row is one stored grant—for example a scoped editor has separate rows for
              branch page, branch news, and branch projects.
            </p>
            {filteredRows.length === 0 ? (
              <p className="rounded border border-dashed border-brand-gray bg-brand-gray/5 px-4 py-6 text-center text-sm text-brand-blue/70">
                No rows match your filters — try clearing filters or widening the search.
              </p>
            ) : (
              <div className="max-h-[min(20rem,50vh)] overflow-auto rounded-lg border border-brand-gray/80">
                <table className="w-full min-w-[20rem] border-collapse text-left text-sm">
                  <thead>
                    <tr className="border-b border-brand-gray bg-brand-gray/25 text-xs font-semibold uppercase tracking-wide text-brand-blue/85">
                      <th className="whitespace-nowrap px-3 py-3 sm:px-4">Email</th>
                      <th className="whitespace-nowrap px-3 py-3 sm:px-4">Permission</th>
                      <th className="min-w-[8rem] px-3 py-3 sm:px-4">Scope</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-brand-gray/60">
                    {filteredRows.map((r, i) => (
                      <tr key={r.id} className={i % 2 === 0 ? "bg-white" : "bg-brand-gray/5"}>
                        <td className="whitespace-nowrap px-3 py-2.5 font-medium text-brand-blue sm:px-4">{r.email}</td>
                        <td className="px-3 py-2.5 text-brand-blue/90 sm:px-4">{CAP_LABELS[r.capability]}</td>
                        <td className="px-3 py-2.5 text-xs text-brand-blue/70 sm:px-4">{scopeLine(r)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </details>
        ) : null}
        </section>
        </div>

        <form
          onSubmit={onSubmit}
          className="min-w-0 space-y-4 rounded-xl border border-brand-gray bg-white p-5 shadow-sm lg:min-h-0"
        >
        <h3 className="font-display text-lg font-bold">{editingEmail ? `Edit ${editingEmail}` : "Add or replace access"}</h3>
        <div>
          <label htmlFor="pu-email" className="block text-xs font-semibold text-brand-blue">
            Email
          </label>
          <input
            id="pu-email"
            type="email"
            required
            value={emailInput}
            onChange={(e) => setEmailInput(e.target.value.trim().toLowerCase())}
            disabled={!!editingEmail}
            className="mt-1 block w-full rounded border border-brand-gray px-3 py-2 text-sm disabled:bg-brand-gray/50"
          />
          {editingEmail ? (
            <p className="mt-1 text-xs text-brand-blue/60">Email is fixed while editing; use Clear then add to use another address.</p>
          ) : null}
        </div>

        <fieldset className="space-y-3">
          <legend className="text-xs font-bold uppercase tracking-wide text-brand-blue/70">Access type</legend>
          <label className="flex cursor-pointer items-start gap-2 text-sm">
            <input
              type="radio"
              name="accessKind"
              className="mt-1"
              checked={accessKind === "portal_manager"}
              onChange={() => chooseAccessKind("portal_manager")}
            />
            <span>
              <span className="font-semibold">{CAP_LABELS.manage_users}</span>
              <span className="block text-xs text-brand-blue/70">
                Full access to all branches, batches, and associations, including who may use this admin portal.
              </span>
            </span>
          </label>
          <label className="flex cursor-pointer items-start gap-2 text-sm">
            <input
              type="radio"
              name="accessKind"
              className="mt-1"
              checked={accessKind === "site_content_admin"}
              onChange={() => chooseAccessKind("site_content_admin")}
            />
            <span>
              <span className="font-semibold">{CAP_LABELS.site_admin}</span>
              <span className="block text-xs text-brand-blue/70">
                Edit any branch, batch, or association content (and site-wide news/projects), but not portal users.
              </span>
            </span>
          </label>
          <label className="flex cursor-pointer items-start gap-2 text-sm">
            <input
              type="radio"
              name="accessKind"
              className="mt-1"
              checked={accessKind === "tasks_access"}
              onChange={() => chooseAccessKind("tasks_access")}
            />
            <span>
              <span className="font-semibold">{CAP_LABELS.tasks}</span>
              <span className="block text-xs text-brand-blue/70">
                View and update the shared Tasks page. Changes are attributed to this user.
              </span>
            </span>
          </label>
          <label className="flex cursor-pointer items-start gap-2 text-sm">
            <input
              type="radio"
              name="accessKind"
              className="mt-1"
              checked={accessKind === "scoped"}
              onChange={() => chooseAccessKind("scoped")}
            />
            <span>
              <span className="font-semibold">Scoped editor</span>
              <span className="block text-xs text-brand-blue/70">
                Assign a branch, a batch, and/or associations below. Each assignment includes full editing (page, news, projects).
              </span>
            </span>
          </label>
        </fieldset>

        {accessKind === "portal_manager" ? (
          <p className="rounded border border-brand-gray/80 bg-brand-gray/10 px-3 py-2 text-sm text-brand-blue/80">
            No branch, batch, or association pickers are needed—this role already covers the whole organisation.
          </p>
        ) : null}

        {accessKind === "site_content_admin" ? (
          <p className="rounded border border-brand-gray/80 bg-brand-gray/10 px-3 py-2 text-sm text-brand-blue/80">
            No entity pickers are needed. This person cannot open the Users tab to change portal access.
          </p>
        ) : null}

        {accessKind === "tasks_access" ? (
          <p className="rounded border border-brand-gray/80 bg-brand-gray/10 px-3 py-2 text-sm text-brand-blue/80">
            No entity pickers are needed. This access is global and only applies to /tasks.
          </p>
        ) : null}

        {showScoped ? (
          <p className="rounded border border-brand-gray/80 bg-brand-gray/10 px-3 py-2 text-sm text-brand-blue/80">
            Use only the sections that apply. You need at least one branch, batch, or association. Each choice grants full editing
            there—no separate permission checkboxes.
          </p>
        ) : null}

        {showScoped ? (
          <fieldset className="space-y-2 rounded-lg border border-brand-gray/60 bg-brand-gray/5 p-3">
            <legend className="text-xs font-bold uppercase tracking-wide text-brand-blue/70">Branch</legend>
            <label className="block text-xs font-semibold text-brand-blue">
              Branch
              <select
                className="mt-1 block w-full rounded border border-brand-gray px-2 py-1.5 text-sm"
                value={branchIds[0] ?? ""}
                onChange={(e) => setBranchIds(e.target.value ? [e.target.value] : [])}
              >
                <option value="">Select…</option>
                {branches.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </select>
            </label>
            <p className="text-xs text-brand-blue/70">
              Full branch access: page, news, and projects (applied automatically when you save).
            </p>
          </fieldset>
        ) : null}

        {showScoped ? (
          <fieldset className="space-y-2 rounded-lg border border-brand-gray/60 bg-brand-gray/5 p-3">
            <legend className="text-xs font-bold uppercase tracking-wide text-brand-blue/70">Batch</legend>
            <label className="block text-xs font-semibold text-brand-blue">
              Batch
              <select
                className="mt-1 block w-full rounded border border-brand-gray px-2 py-1.5 text-sm"
                value={batchIds[0] ?? ""}
                onChange={(e) => setBatchIds(e.target.value ? [e.target.value] : [])}
              >
                <option value="">Select…</option>
                {batches.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.label}
                  </option>
                ))}
              </select>
            </label>
            <p className="text-xs text-brand-blue/70">
              Full batch access: page, news, and projects (applied automatically when you save).
            </p>
          </fieldset>
        ) : null}

        {showScoped ? (
          <fieldset className="space-y-2 rounded-lg border border-brand-gray/60 bg-brand-gray/5 p-3">
            <legend className="text-xs font-bold uppercase tracking-wide text-brand-blue/70">Associations</legend>
            <p className="text-xs text-brand-blue/70">
              Hold Ctrl/Cmd to select multiple. Each selected group gets full access: page, news, and projects.
            </p>
            <select
              multiple
              value={assocIds}
              onChange={(e) => setAssocIds(Array.from(e.target.selectedOptions).map((o) => o.value))}
              className="block min-h-[6rem] w-full rounded border border-brand-gray px-2 py-1 text-sm"
              size={Math.min(8, Math.max(3, associations.length))}
            >
              {associations.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </select>
          </fieldset>
        ) : null}

        {msg ? (
          <p className={`text-sm ${msg === "Saved." ? "text-green-800" : "text-brand-maroon"}`}>{msg}</p>
        ) : null}

        <div className="flex flex-wrap gap-2">
          <button
            type="submit"
            className="rounded-full bg-brand-maroon px-6 py-2 text-sm font-bold uppercase tracking-wide text-white"
          >
            Save access
          </button>
          <button type="button" className={adminAddNewSecondaryButtonClass} onClick={startNew}>
            Clear form
          </button>
        </div>
      </form>
      </div>
    </div>
  );
}
